/**
 * Fades each page in as the route changes: a plain element with a CSS entrance
 * (`.page-enter` in styles.css), re-created whenever the page's key changes so
 * the animation replays. There is no exit animation, so a lazy-loaded page can
 * never hold a navigation up.
 *
 * The page a visitor first lands on does not animate (the home hero shouldn't
 * flash), and an element keeps its entrance class for its whole life, so a
 * re-render while it is still fading never cuts it short.
 */

import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';

/** The key a route animates under: all of /admin is one page, so switching admin tabs doesn't replay it. */
export const pageKey = (pathname: string): string =>
  pathname === '/admin' || pathname.startsWith('/admin/') ? '/admin' : pathname;

function Page({ enter, children }: { enter: boolean; children: ReactNode }) {
  // Read once, when this page mounts: the decision belongs to the element, not to later renders.
  const [animate] = useState(enter);
  return <div className={animate ? 'page-enter' : undefined}>{children}</div>;
}

export default function PageTransition({ children }: { children: ReactNode }) {
  const key = pageKey(useLocation().pathname);
  const previousKey = useRef<string | null>(null);
  const navigated = previousKey.current !== null && previousKey.current !== key;
  useEffect(() => {
    previousKey.current = key;
  }, [key]);
  return (
    <Page key={key} enter={navigated}>
      {children}
    </Page>
  );
}
